import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Gift } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get("ref") || "").toUpperCase();
  const nextFromUrl = searchParams.get("next") || "";
  useSEO({
    title: "Create your Maqam account",
    description: "Sign up for Maqam and apply to 100 Saudi jobs in one click. Smart match ranking, bulk applications, and a private platform inbox.",
    path: "/register",
  });
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    consent_accepted: false,
  });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_accepted) {
      toast.error("Please accept the privacy consent to continue");
      return;
    }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome — your inbox is ${u.platform_email}`);
      
      // Auto-redeem referral code if present in URL
      if (refFromUrl) {
        try {
          const redeemRes = await api.post("/referral/redeem", { code: refFromUrl });
          if (redeemRes.data?.bonus_sends_added) {
            toast.success(`+${redeemRes.data.bonus_sends_added} bonus CV sends added from referral!`);
          }
        } catch (redeemErr) {
          // Silently ignore redeem errors (invalid code, already redeemed, etc.)
          console.log("Referral redeem failed:", redeemErr?.response?.data?.detail);
        }
      }
      
      navigate(nextFromUrl && nextFromUrl.startsWith("/") ? nextFromUrl : "/app/profile");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const status = err?.response?.status;
      if (status === 400 && /already registered/i.test(detail)) {
        toast.error("This email is already registered. Redirecting to Login…");
        setTimeout(() => navigate(`/login?email=${encodeURIComponent(form.email)}`), 1200);
      } else if (!err?.response) {
        toast.error("Network error — please check your connection and try again.");
      } else {
        toast.error(detail || "Registration failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#1a1a1c] text-white">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 md:order-1 bg-[#1a1a1c]">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <h2 className="font-display font-normal text-3xl md:text-4xl tracking-tight mb-2 text-white">{t("sign_up")}</h2>
          <p className="text-white/60 mb-10">
            Already have an account?{" "}
            <Link to="/login" className="link-underline text-white font-medium" data-testid="to-login">
              {t("login")}
            </Link>
          </p>

          {refFromUrl && (
            <div className="mb-8 bg-white/[0.04] border border-white/15 p-4 flex items-center gap-3 rounded-xl" data-testid="ref-banner">
              <Gift className="w-5 h-5 text-white" />
              <div className="text-sm">
                <div className="font-bold text-white">Referral code {refFromUrl} applied</div>
                <div className="text-white/60">You'll receive +5 bonus CV sends ($5 value) when you sign up.</div>
              </div>
            </div>
          )}

          <label className="block mb-5">
            <span className="block font-mono text-xs uppercase tracking-widest text-white/50 mb-2">{t("full_name")}</span>
            <input
              type="text"
              required
              value={form.full_name}
              onChange={change("full_name")}
              className="w-full bg-transparent border-b-2 border-white/20 focus:border-white outline-none py-3 text-lg text-white"
              data-testid="register-name"
            />
          </label>
          <label className="block mb-5">
            <span className="block font-mono text-xs uppercase tracking-widest text-white/50 mb-2">{t("email")}</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={change("email")}
              className="w-full bg-transparent border-b-2 border-white/20 focus:border-white outline-none py-3 text-lg text-white"
              data-testid="register-email"
            />
          </label>
          <label className="block mb-8">
            <span className="block font-mono text-xs uppercase tracking-widest text-white/50 mb-2">{t("password")}</span>
            <input
              type="password"
              required
              minLength={10}
              pattern="(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).{10,}"
              title="At least 10 characters with one uppercase, one lowercase, one number and one special character."
              value={form.password}
              onChange={change("password")}
              className="w-full bg-transparent border-b-2 border-white/20 focus:border-white outline-none py-3 text-lg text-white"
              data-testid="register-password"
            />
            <span className="block text-[11px] text-white/40 mt-2">
              At least 10 characters, with an uppercase letter, a lowercase letter, a number, and a special character.
            </span>
          </label>

          <label className="flex items-start gap-3 mb-8 cursor-pointer">
            <input
              type="checkbox"
              checked={form.consent_accepted}
              onChange={change("consent_accepted")}
              className="mt-1 w-5 h-5 accent-white flex-shrink-0"
              data-testid="register-consent"
            />
            <span className="text-sm text-white/70 leading-relaxed">{t("consent_register")}</span>
          </label>

          <button
            disabled={loading}
            className="w-full bg-white text-[#1a1a1c] hover:bg-white/90 font-bold py-5 flex items-center justify-center gap-3 transition-colors disabled:opacity-60 rounded-full"
            data-testid="register-submit"
          >
            {loading ? "…" : t("get_started")} <ArrowRight className="w-5 h-5" />
          </button>

          <div className="relative my-4" style={{ display: process.env.REACT_APP_GOOGLE_AUTH_ENABLED === "true" ? undefined : "none" }}>
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#1a1a1c] px-3 text-white/40 uppercase tracking-widest">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={async () => {
              try {
                // Same-origin-aware call (prevents cross-domain OAuth bounce).
                const r = await api.get("/oauth/google/login?mode=register");
                const auth_url = r.data?.auth_url;
                if (!auth_url) throw new Error("no auth_url");
                window.location.href = auth_url;
              } catch (err) {
                const status = err?.response?.status;
                if (status === 503) {
                  toast.error("Google sign-in is not configured on the server yet.");
                } else {
                  toast.error("Could not start Google sign-in. Please use email + password.");
                }
                // eslint-disable-next-line no-console
                console.error("Google sign-up failed:", err?.response?.data || err);
              }
            }}
            className="w-full border border-white/20 bg-white/[0.04] hover:bg-white/[0.08] text-white font-medium py-4 flex items-center justify-center gap-3 transition-colors rounded-full"
            data-testid="google-signup-btn"
            style={{ display: process.env.REACT_APP_GOOGLE_AUTH_ENABLED === "true" ? undefined : "none" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
        </form>
      </div>

      <div className="hidden md:flex p-12 flex-col justify-between order-1 md:order-2 relative border-l border-white/10">
        <Link to="/" className="flex items-center gap-1 w-fit">
          <span className="font-display font-black text-2xl tracking-tighter inline-flex items-center">
            <span className="border border-white px-1.5 py-0.5 mr-[2px] leading-none">M</span>
            <span>aqam</span>
          </span>
        </Link>
        <div>
          <h1 className="font-display font-normal text-5xl lg:text-6xl tracking-tight leading-[1]">
            Your private
            <br />
            <span className="italic font-light text-white/90">Maqam inbox.</span>
          </h1>
          <p className="text-white/60 text-lg mt-6 max-w-md font-light">
            We generate a platform email like <span className="font-mono text-white">yourname@maqam-ksa.com</span> — every reply
            lands here. Your personal email stays private.
          </p>
          <div className="mt-10 flex items-center gap-3 text-sm text-white/60">
            <ShieldCheck className="w-5 h-5 text-white" />
            PDPL-aligned • revocable consent
          </div>
        </div>
        <div className="font-mono text-xs text-white/40 uppercase tracking-widest">/ 100 apps in one click</div>
      </div>
    </div>
  );
}
